import { Check, KeyRound, Loader2, Plus, RefreshCw, Search, Upload, UserMinus, UserPlus, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useState, type ChangeEvent, type FormEvent } from "react";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import { useHeaderActions } from "../../context/HeaderActionsContext";
import { ALL_TAB_KEYS, IAM_TAB, TAB_REGISTRY, TAB_AUDIENCES, addTeamPreset, getTabDef, type TabKey } from "../../lib/tab-registry";
import { Button } from "../ui/button";
import { Card } from "../ui/card";
import { Checkbox } from "../ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Pagination } from "../users-config/Pagination";
import { getAdminCsvTemplate, parseAdminCsv } from "./admin-csv";
import { AdminTable } from "./AdminTable";
import { iamApi, type AdminAccount, type BulkAdminResult, type NewAdmin, type PromoteCandidate, iamErrorMessage } from "./iam-api";
import { candidateView, type CandidateSearch } from "./candidate-search";

const PAGE_SIZE = 50;

/**
 * Tab picker used by the editor and both create dialogs. `lockIam` disables
 * the iam box: the server refuses removing your own iam either way, this just
 * keeps the UI honest about it.
 */
const TabGrid = ({
  value,
  onChange,
  lockIam,
  disabled,
}: {
  value: TabKey[];
  onChange: (next: TabKey[]) => void;
  lockIam?: boolean;
  disabled?: boolean;
}) => {
  const toggle = (tab: TabKey, checked: boolean) => {
    const next = new Set(value);
    if (checked) next.add(tab);
    else next.delete(tab);
    onChange(ALL_TAB_KEYS.filter((t) => next.has(t)));
  };
  const group = (title: string, tabs: TabKey[]) => (
    <div>
      <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">{title}</p>
      <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 sm:grid-cols-3">
        {tabs.map((tab) => {
          const locked = lockIam && tab === IAM_TAB;
          return (
            <label key={tab} className={`flex items-center gap-2 text-sm ${locked ? "opacity-60" : ""}`}>
              <Checkbox
                checked={value.includes(tab)}
                disabled={disabled || locked}
                onCheckedChange={(checked) => toggle(tab, checked === true)}
              />
              <span>{getTabDef(tab).label}</span>
            </label>
          );
        })}
      </div>
    </div>
  );
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs text-muted-foreground">Add team tabs:</span>
        <Button type="button" size="sm" variant="outline" disabled={disabled} onClick={() => onChange(addTeamPreset(value, "support"))}>Support</Button>
        <Button type="button" size="sm" variant="outline" disabled={disabled} onClick={() => onChange(addTeamPreset(value, "engineering"))}>Engineering</Button>
      </div>
      {TAB_AUDIENCES.map((audience) => <div key={audience.label}>{group(audience.label, TAB_REGISTRY.filter((tab) => tab.audience === audience.id).map((tab) => tab.id))}</div>)}
      <p className="text-xs text-muted-foreground">Presets add tabs to your selection. Other tools and Dashboard IAM are chosen individually.</p>
      <div className="flex gap-2">
        <Button type="button" size="sm" variant="ghost" disabled={disabled} onClick={() => onChange(ALL_TAB_KEYS.filter((tab) => tab !== IAM_TAB || value.includes(IAM_TAB)))}>
          All tools
        </Button>
        <Button type="button" size="sm" variant="ghost" disabled={disabled} onClick={() => onChange(lockIam && value.includes(IAM_TAB) ? [IAM_TAB] : [])}>
          None
        </Button>
      </div>
    </div>
  );
};

const AdminForm = ({
  value,
  onChange,
  disabled,
}: {
  value: NewAdmin;
  onChange: (next: NewAdmin) => void;
  disabled?: boolean;
}) => (
  <div className="space-y-3">
    <div className="grid gap-3 sm:grid-cols-2">
      <div className="space-y-1.5">
        <Label htmlFor="iam-name">Name</Label>
        <Input id="iam-name" value={value.name} disabled={disabled} onChange={(e) => onChange({ ...value, name: e.target.value })} required />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="iam-email">Email</Label>
        <Input id="iam-email" type="email" value={value.email} disabled={disabled} onChange={(e) => onChange({ ...value, email: e.target.value })} required />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="iam-username">Username (optional)</Label>
        <Input id="iam-username" value={value.username ?? ""} disabled={disabled} onChange={(e) => onChange({ ...value, username: e.target.value || undefined })} />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="iam-password">Temporary password</Label>
        <Input id="iam-password" type="password" value={value.password} disabled={disabled} onChange={(e) => onChange({ ...value, password: e.target.value })} minLength={8} required />
      </div>
    </div>
    <div className="space-y-1.5">
      <Label>Tabs</Label>
      <TabGrid value={value.permissions} onChange={(permissions) => onChange({ ...value, permissions })} disabled={disabled} />
    </div>
  </div>
);

const emptyAdmin = (): NewAdmin => ({ name: "", email: "", password: "", permissions: [] });

export const DashboardIam = () => {
  const { userId, refreshSession } = useAuth();
  const myId = userId ? Number(userId) : null;
  const { setActions } = useHeaderActions();

  const [admins, setAdmins] = useState<AdminAccount[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [query, setQuery] = useState("");
  const [busyId, setBusyId] = useState<number | null>(null);

  const [editing, setEditing] = useState<AdminAccount | null>(null);
  const [draft, setDraft] = useState<TabKey[]>([]);
  const [saving, setSaving] = useState(false);

  const [createOpen, setCreateOpen] = useState(false);
  const [createDraft, setCreateDraft] = useState<NewAdmin>(emptyAdmin());
  const [creating, setCreating] = useState(false);

  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkUsers, setBulkUsers] = useState<NewAdmin[]>([]);
  const [bulkErrors, setBulkErrors] = useState<string[]>([]);
  const [bulkResults, setBulkResults] = useState<BulkAdminResult[] | null>(null);
  const [bulkRunning, setBulkRunning] = useState(false);

  const [promoteOpen, setPromoteOpen] = useState(false);
  const [candidateQuery, setCandidateQuery] = useState("");
  const [search, setSearch] = useState<CandidateSearch | null>(null);
  const [candidate, setCandidate] = useState<PromoteCandidate | null>(null);
  const [promoteTabs, setPromoteTabs] = useState<TabKey[]>([]);
  const [promoting, setPromoting] = useState(false);

  const [demoting, setDemoting] = useState<AdminAccount | null>(null);

  // Bumped to re-run the list effect (after a create/promote/demote or the
  // refresh button). The list is loading while the key of the last finished
  // fetch differs from the key of the wanted one.
  const [reloadTick, setReloadTick] = useState(0);
  const load = useCallback(() => setReloadTick((t) => t + 1), []);
  const requestKey = `${page}|${query.trim()}|${reloadTick}`;
  const [loadedKey, setLoadedKey] = useState<string | null>(null);
  const loading = loadedKey !== requestKey;

  useEffect(() => {
    let cancelled = false;
    iamApi
      .listAdmins({ limit: PAGE_SIZE, offset: (page - 1) * PAGE_SIZE, query: query.trim() || undefined })
      .then((result) => {
        if (cancelled) return;
        setAdmins(result.admins);
        setTotal(result.total);
      })
      .catch((err: unknown) => {
        if (!cancelled) toast.error(err instanceof Error ? err.message : "Failed to load admins");
      })
      .finally(() => {
        if (!cancelled) setLoadedKey(requestKey);
      });
    return () => {
      cancelled = true;
    };
  }, [page, query, requestKey]);

  useEffect(() => {
    setActions(
      <>
        <Button variant="outline" size="icon" onClick={load} disabled={loading} className="h-8 w-8" title="Refresh">
          <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
        </Button>
        <Button variant="outline" onClick={() => setPromoteOpen(true)} className="h-8 gap-2">
          <UserPlus className="h-4 w-4" />
          Promote existing user
        </Button>
        <Button variant="outline" onClick={() => setBulkOpen(true)} className="h-8 gap-2">
          <Upload className="h-4 w-4" />
          Bulk add
        </Button>
        <Button onClick={() => setCreateOpen(true)} className="h-8 gap-2 bg-primary text-primary-foreground hover:bg-primary/90">
          <Plus className="h-4 w-4" />
          Add admin
        </Button>
      </>,
    );
    return () => setActions(null);
  }, [setActions, load, loading]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const showingFrom = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const showingTo = Math.min(page * PAGE_SIZE, total);
  const hasSearch = query.trim().length > 0;
  const iamHolders = useMemo(() => admins.filter((a) => a.permissions.includes(IAM_TAB)).length, [admins]);

  const openEditor = (admin: AdminAccount) => {
    setEditing(admin);
    setDraft(admin.permissions);
  };

  const saveEditor = async () => {
    if (!editing) return;
    setSaving(true);
    setBusyId(editing.id);
    try {
      const result = await iamApi.replacePermissions(editing.id, draft);
      setAdmins((prev) => prev.map((a) => (a.id === editing.id ? { ...a, permissions: result.permissions, lastChangedAt: new Date().toISOString(), lastChangedBy: myId } : a)));
      toast.success(`Updated tabs for ${editing.email}`);
      setEditing(null);
      // The sidebar reads the session, not this table.
      if (editing.id === myId) void refreshSession();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to update permissions");
    } finally {
      setSaving(false);
      setBusyId(null);
    }
  };

  const submitCreate = async (event: FormEvent) => {
    event.preventDefault();
    setCreating(true);
    try {
      await iamApi.createAdmin({ ...createDraft, email: createDraft.email.trim(), name: createDraft.name.trim() });
      toast.success(`Created admin ${createDraft.email}`);
      setCreateOpen(false);
      setCreateDraft(emptyAdmin());
      load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to create admin");
    } finally {
      setCreating(false);
    }
  };

  const onBulkFile = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const parsed = parseAdminCsv(await file.text());
    setBulkUsers(parsed.users);
    setBulkErrors(parsed.errors.map((e) => (e.row ? `Row ${e.row}: ${e.message}` : e.message)));
    setBulkResults(null);
  };

  const downloadTemplate = () => {
    const blob = new Blob([getAdminCsvTemplate()], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "bulk-admin-template.csv";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const runBulk = async () => {
    if (bulkUsers.length === 0) return;
    setBulkRunning(true);
    try {
      const { results } = await iamApi.bulkCreateAdmins(bulkUsers);
      setBulkResults(results);
      const ok = results.filter((r) => r.ok).length;
      toast[ok === results.length ? "success" : "warning"](`${ok} of ${results.length} admins created`);
      load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Bulk create failed");
    } finally {
      setBulkRunning(false);
    }
  };

  useEffect(() => {
    if (!promoteOpen) return;
    const q = candidateQuery.trim();
    // Only the latest query may fill the list. A slower answer for an older
    // query, or one that lands after the dialog closed, is dropped: a row in
    // this list is one click from a promotion.
    let current = true;
    if (q.length < 2) return;
    const handle = setTimeout(() => {
      iamApi
        .searchCandidates(q)
        .then((r) => {
          if (current) setSearch({ q, users: r.users });
        })
        .catch((err) => {
          if (current) setSearch({ q, error: err instanceof Error ? err.message : "Search failed" });
        });
    }, 250);
    return () => {
      current = false;
      clearTimeout(handle);
    };
  }, [candidateQuery, promoteOpen]);

  // Every way out of the promote dialog clears the same four things, so no
  // stale row, selection, or tab set survives into the next opening.
  const resetPromote = () => {
    setPromoteOpen(false);
    setCandidate(null);
    setCandidateQuery("");
    setSearch(null);
    setPromoteTabs([]);
  };
  const view = candidateView(search, candidateQuery);

  const runPromote = async () => {
    if (!candidate) return;
    setPromoting(true);
    try {
      await iamApi.promote(Number(candidate.id), promoteTabs);
      toast.success(`${candidate.email} is now a dashboard admin`);
      resetPromote();
      load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to promote");
    } finally {
      setPromoting(false);
    }
  };

  const runDemote = async () => {
    if (!demoting) return;
    setBusyId(demoting.id);
    try {
      await iamApi.demote(demoting.id);
      toast.success(`${demoting.email} is no longer a dashboard admin`);
      setDemoting(null);
      load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to demote");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="flex h-full flex-col gap-4 overflow-hidden">
      {/* Search bar */}
      <Card className="rounded-xl border-border/60 p-4 shadow-sm">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative min-w-[200px] max-w-md flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search by name, email, or username..."
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setPage(1);
              }}
              className="pl-9"
            />
          </div>
          <div className="flex items-center gap-3 text-sm text-muted-foreground">
            <span>{total} admins</span>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-lime-600/10 px-2.5 py-0.5 text-xs font-medium text-lime-900 ring-1 ring-lime-600/20">
              <KeyRound className="h-3 w-3" />
              {iamHolders} with IAM on this page
            </span>
          </div>
        </div>
      </Card>

      <Card className="flex min-h-0 flex-1 flex-col overflow-hidden border shadow-sm">
        <AdminTable
          admins={admins}
          myId={myId}
          actionAdminId={busyId}
          isLoading={loading}
          hasSearch={hasSearch}
          onClearSearch={() => {
            setQuery("");
            setPage(1);
          }}
          onEditTabs={openEditor}
          onDemote={setDemoting}
        />
        <Pagination
          page={page}
          totalPages={totalPages}
          showingFrom={showingFrom}
          showingTo={showingTo}
          total={total}
          onPageChange={setPage}
        />
      </Card>

      {/* Edit tabs */}
      <Dialog open={editing !== null} onOpenChange={(open) => !open && !saving && setEditing(null)}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Tabs for {editing?.email}</DialogTitle>
            <DialogDescription>
              Holding a tab allows every action on it. Changes take effect on the user's next request.
              {editing?.id === myId ? " You cannot remove your own Dashboard IAM." : ""}
            </DialogDescription>
          </DialogHeader>
          <TabGrid value={draft} onChange={setDraft} lockIam={editing?.id === myId} disabled={saving} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} disabled={saving}>Cancel</Button>
            <Button onClick={() => void saveEditor()} disabled={saving} className="gap-2">
              {saving ? <Loader2 className="size-4 animate-spin" /> : <Check className="size-4" />} Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create admin */}
      <Dialog open={createOpen} onOpenChange={(open) => !open && !creating && setCreateOpen(false)}>
        <DialogContent className="sm:max-w-2xl">
          <form onSubmit={submitCreate}>
            <DialogHeader>
              <DialogTitle>Add admin</DialogTitle>
              <DialogDescription>Creates a staff account with the chosen tabs. The account is created first, then granted, then marked admin.</DialogDescription>
            </DialogHeader>
            <div className="py-4"><AdminForm value={createDraft} onChange={setCreateDraft} disabled={creating} /></div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCreateOpen(false)} disabled={creating}>Cancel</Button>
              <Button type="submit" disabled={creating} className="gap-2">
                {creating ? <Loader2 className="size-4 animate-spin" /> : <Plus className="size-4" />} Create
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Bulk add */}
      <Dialog open={bulkOpen} onOpenChange={(open) => { if (!open && !bulkRunning) { setBulkOpen(false); setBulkUsers([]); setBulkErrors([]); setBulkResults(null); } }}>
        <DialogContent className="sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle>Bulk add admins</DialogTitle>
            <DialogDescription>CSV columns: name, email, password, username, dashboard_tabs (pipe-separated tab ids). Every row becomes an admin.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-3">
              <Input type="file" accept=".csv,text/csv" onChange={onBulkFile} className="max-w-sm" disabled={bulkRunning} />
              <Button type="button" variant="outline" size="sm" onClick={downloadTemplate}>Download template</Button>
              <span className="text-xs text-muted-foreground">Tab ids: {ALL_TAB_KEYS.join(", ")}</span>
            </div>
            {bulkErrors.length > 0 ? (
              <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
                <ul className="list-disc pl-5">{bulkErrors.slice(0, 10).map((e, i) => <li key={i}>{e}</li>)}</ul>
                {bulkErrors.length > 10 ? <p className="mt-1 text-xs">+{bulkErrors.length - 10} more</p> : null}
              </div>
            ) : null}
            {bulkUsers.length > 0 && !bulkResults ? (
              <p className="text-sm text-muted-foreground">{bulkUsers.length} admins ready to create.</p>
            ) : null}
            {bulkResults ? (
              <div className="max-h-56 overflow-auto rounded-md border p-2 text-sm">
                {bulkResults.map((r) => (
                  <div key={r.email} className="flex items-center gap-2 py-0.5">
                    {r.ok ? <Check className="size-4 text-lime-700" /> : <X className="size-4 text-destructive" />}
                    <span>{r.email}</span>
                    {!r.ok ? <span className="text-xs text-destructive">{iamErrorMessage({ error: r.error }, r.error)}</span> : null}
                  </div>
                ))}
              </div>
            ) : null}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setBulkOpen(false)} disabled={bulkRunning}>Close</Button>
            <Button type="button" onClick={() => void runBulk()} disabled={bulkRunning || bulkUsers.length === 0 || bulkErrors.length > 0} className="gap-2">
              {bulkRunning ? <Loader2 className="size-4 animate-spin" /> : <Upload className="size-4" />} Create {bulkUsers.length || ""}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Promote existing user */}
      <Dialog open={promoteOpen} onOpenChange={(open) => { if (!open && !promoting) resetPromote(); }}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Promote existing user</DialogTitle>
            <DialogDescription>Turns a product account into a staff account with the chosen tabs. Grants are written first, then the account is marked admin.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {candidate ? (
              <div className="flex items-center justify-between rounded-md border p-2 text-sm">
                <span><span className="font-medium">{candidate.name || candidate.email}</span> <span className="text-muted-foreground">{candidate.email}</span></span>
                <Button size="sm" variant="ghost" onClick={() => setCandidate(null)} disabled={promoting}>Change</Button>
              </div>
            ) : (
              <div className="space-y-2">
                <Input placeholder="Search by email, name, or username…" value={candidateQuery} onChange={(e) => setCandidateQuery(e.target.value)} autoFocus />
                {view.kind === "rows" ? (
                  <div className="max-h-48 overflow-auto rounded-md border">
                    {view.users.map((c) => (
                      <button key={c.id} type="button" className="flex w-full items-center justify-between px-3 py-1.5 text-left text-sm hover:bg-muted" onClick={() => setCandidate(c)}>
                        <span>{c.name || c.email}</span>
                        <span className="text-xs text-muted-foreground">{c.email}</span>
                      </button>
                    ))}
                  </div>
                ) : view.kind === "searching" ? (
                  <p className="text-xs text-muted-foreground">Searching…</p>
                ) : view.kind === "error" ? (
                  <p className="text-xs text-destructive">{view.message}</p>
                ) : view.kind === "empty" ? (
                  <p className="text-xs text-muted-foreground">No matching non-admin accounts.</p>
                ) : null}
              </div>
            )}
            <div className="space-y-1.5">
              <Label>Tabs</Label>
              <TabGrid value={promoteTabs} onChange={setPromoteTabs} disabled={promoting} />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={resetPromote} disabled={promoting}>Cancel</Button>
            <Button type="button" onClick={() => void runPromote()} disabled={promoting || !candidate} className="gap-2">
              {promoting ? <Loader2 className="size-4 animate-spin" /> : <UserPlus className="size-4" />} Promote
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Demote confirm */}
      <Dialog open={demoting !== null} onOpenChange={(open) => !open && setDemoting(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Demote {demoting?.email}?</DialogTitle>
            <DialogDescription>Removes every tab, signs them out everywhere, and turns the account back into a regular product account. The server refuses if this would leave nobody with Dashboard IAM.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDemoting(null)}>Cancel</Button>
            <Button variant="destructive" onClick={() => void runDemote()} disabled={busyId !== null} className="gap-2">
              {busyId !== null ? <Loader2 className="size-4 animate-spin" /> : <UserMinus className="size-4" />} Demote
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
